import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { screensAPI } from '@/lib/api'
import usePageTitle from '@/lib/usePageTitle'

/* Publieke renderer voor eigen inhouden — leest slug uit URL, haalt board op,
   en toont het volgens z'n kind: media / announcement / slideshow / embed. */
export default function CustomBoardPage() {
  const { slug } = useParams()
  const [board, setBoard] = useState(null)
  const [err, setErr]     = useState(null)

  usePageTitle(board?.name || slug)

  useEffect(() => {
    let stop = false
    const laad = () => screensAPI.getBoardBySlug(slug)
      .then(r => { if (!stop) { setBoard(r.data?.board ?? null); setErr(null) } })
      .catch(e => { if (!stop) setErr(e.response?.data?.error ?? e.message) })
    laad()
    const t = setInterval(laad, 15_000)   // pak wijzigingen mee zonder refresh
    return () => { stop = true; clearInterval(t) }
  }, [slug])

  if (err)   return <Boodschap tekst={`Fout: ${err}`} />
  if (!board) return <Boodschap tekst="Laden…" />

  const config = board.config || {}
  switch (board.kind) {
    case 'media':        return <MediaView   config={config} />
    case 'announcement': return <NoticeView  config={config} />
    case 'slideshow':    return <SlideView   config={config} />
    case 'embed':        return <EmbedView   config={config} />
    default:             return <Boodschap tekst={`Onbekend soort: ${board.kind}`} />
  }
}

function Boodschap({ tekst }) {
  return (
    <div className="min-h-screen bg-background text-foreground grid place-items-center p-8">
      <p className="text-sm text-muted-foreground">{tekst}</p>
    </div>
  )
}

/* ── Media ─────────────────────────────────────────────────────────
   Toont een foto of video fullscreen. `mode` = 'contain' (met letterbox)
   of 'cover' (bijsnijden). Video kan muted + looping draaien.        */
export function MediaView({ config }) {
  const url = config.url || ''
  const mode = config.mode === 'cover' ? 'cover' : 'contain'
  const isVideo = /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(url)
  const style = { position: 'fixed', inset: 0, width: '100vw', height: '100vh',
                  background: config.bg || '#000', objectFit: mode }

  if (!url) return <Boodschap tekst="Nog geen media-URL ingesteld." />
  if (isVideo) {
    return (
      <video src={url} autoPlay muted={config.video_muted !== false}
             loop={config.video_loop !== false} playsInline style={style} />
    )
  }
  return <img src={url} alt="" style={style} />
}

/* ── Aankondiging ─────────────────────────────────────────────────
   Grote titel + subtekst, volledig instelbaar in kleuren.           */
export function NoticeView({ config }) {
  const bg    = config.bg_color   || '#0B0B0B'
  const kleur = config.text_color || '#B4FF39'
  const groot = config.size === 'huge'
  return (
    <div style={{
      position: 'fixed', inset: 0, background: bg, color: kleur,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '4rem', textAlign: 'center',
    }}>
      {config.eyebrow && (
        <div style={{
          fontFamily: 'ui-monospace, monospace', fontSize: 'clamp(0.9rem, 2vw, 1.5rem)',
          letterSpacing: '0.3em', textTransform: 'uppercase', opacity: 0.7,
          marginBottom: '2rem',
        }}>{config.eyebrow}</div>
      )}
      <div style={{
        fontWeight: 800, lineHeight: 0.95,
        fontSize: groot ? 'clamp(6rem, 22vw, 24rem)' : 'clamp(3rem, 12vw, 12rem)',
        letterSpacing: '-0.02em',
      }}>{config.title || 'Titel'}</div>
      {config.subtitle && (
        <div style={{
          fontSize: 'clamp(1.5rem, 4vw, 4rem)', marginTop: '2rem', opacity: 0.85,
          maxWidth: '80vw',
        }}>{config.subtitle}</div>
      )}
    </div>
  )
}

/* ── Embed ─────────────────────────────────────────────────────────
   Laadt een willekeurige externe (of interne) URL in een iframe.    */
export function EmbedView({ config }) {
  if (!config.url) return <Boodschap tekst="Nog geen URL ingesteld." />
  return (
    <iframe src={config.url} title="embed"
            style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh',
                     border: 'none', background: '#000' }}
            sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
            allow="autoplay; fullscreen" />
  )
}

/* ── Slideshow ────────────────────────────────────────────────────
   Reeks van items. Elk item = { kind: 'media'|'announcement'|'embed', config }.
   Wisselt om de `interval_seconds` (default 8). */
export function SlideView({ config }) {
  const items = Array.isArray(config.items) ? config.items : []
  const interval = Math.max(3, Number(config.interval_seconds) || 8) * 1000
  const [i, setI] = useState(0)

  useEffect(() => {
    if (items.length < 2) return
    const t = setInterval(() => setI(p => (p + 1) % items.length), interval)
    return () => clearInterval(t)
  }, [items.length, interval])

  if (!items.length) return <Boodschap tekst="Slideshow is leeg." />
  const item = items[i]
  if (!item) return <Boodschap tekst="Item niet gevonden." />

  const inner =
    item.kind === 'media'        ? <MediaView   config={item.config || {}} /> :
    item.kind === 'announcement' ? <NoticeView  config={item.config || {}} /> :
    item.kind === 'embed'        ? <EmbedView   config={item.config || {}} /> :
    <Boodschap tekst={`Onbekend item-soort: ${item.kind}`} />

  return inner
}
